import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';

function ControlCreationWizard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { fileId, filename, file } = location.state || {};

    const [formData, setFormData] = useState({
        title: '',
        objective: '',
        controlType: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.objective || !formData.controlType) {
            // Basic validation
            return;
        }

        setIsSubmitting(true);
        try {
            const userHash = localStorage.getItem('userHash');
            const payload = {
                uploadId: fileId, // Link to the uploaded file
                title: formData.title,
                objective: formData.objective,
                controlType: formData.controlType
            };

            // Proxy through Go backend
            await axiosInstance.post('/api/controls/draft', payload);

            // Navigate to test steps generation with data
            navigate('/sox-controls/create-wizard/test-steps', {
                state: {
                    controlData: { title: formData.title, objective: formData.objective, controlType: formData.controlType },
                    fileId: fileId,
                    userHash: userHash,
                    file: file // Pass the raw file object
                }
            });

        } catch (error) {
            console.error('Failed to create draft:', error);
            if (error?.response?.status === 403) {
                alert(
                    'Failed to create draft: ' +
                        resolve403ErrorMessage(error, error.response?.data?.error || error.message)
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Top Bar */}
            <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/sox-controls/new')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 text-gray-500" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Control Creation Wizard</h1>
                        <p className="text-sm text-gray-500">Follow the steps to create and configure your control</p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/sox-controls')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                    Cancel
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 max-w-2xl mx-auto w-full px-8 py-12">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-8">
                        {/* Step Indicator */}
                        <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-6">
                            <div className="flex items-center gap-2">
                                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">Step 1 of 3</span>
                            </div>
                            <span className="text-sm text-gray-500">Control Information</span>
                        </div>

                        {/* The following content was likely intended as a comment or external note,
                            but was provided as part of a code edit. It has been commented out
                            to maintain syntactically correct JSX. */}
                        {/*
                        <h2 className="text-![Filled Wizard Form](/Users/taikicoleman/.gemini/antigravity/brain/8790c6d7-3cfb-4102-97e4-adba1c175dc2/filled_wizard_form_1763616992692.png)

                        ### UI Refinement
                        I adjusted the proportions of the Wizard form to match your design more closely. I reduced the container width and the vertical padding of the input fields.

                        ![Refined Wizard UI](/Users/taikicoleman/.gemini/antigravity/brain/8790c6d7-3cfb-4102-97e4-adba1c175dc2/refined_wizard_ui_1763617075747.png)
                        >
                        */}
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">Create Control Input Form</h2>
                        <p className="text-sm text-gray-500 mb-8">Capture key control details to get started</p>

                        <div className="space-y-6">
                            {/* Control Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    Control Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="e.g., Three-Way Match Control"
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                                />
                            </div>

                            {/* Control Objective */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    Control Objective <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="objective"
                                    value={formData.objective}
                                    onChange={handleChange}
                                    placeholder="Describe the objective of this control..."
                                    rows={4}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                                />
                            </div>

                            {/* Control Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    Control Type <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        name="controlType"
                                        value={formData.controlType}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all appearance-none cursor-pointer text-sm"
                                    >
                                        <option value="" disabled>Select control type</option>
                                        <option value="automated">Automated</option>
                                        <option value="manual">Manual</option>
                                        <option value="it-dependent-manual">IT Dependent Manual</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-10 flex justify-end">
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || !formData.title || !formData.objective || !formData.controlType}
                                className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg flex items-center gap-2 transition-colors ${isSubmitting || !formData.title || !formData.objective || !formData.controlType
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {isSubmitting ? 'Saving...' : 'Next'}
                                {!isSubmitting && (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* Help Button */}
            <div className="fixed bottom-8 right-8">
                <button className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-blue-700 transition-colors">
                    <HelpCircle className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
}

export default ControlCreationWizard;
